import { confirmAuthCompletion, type AuthCompletion } from './authCompletion.js';
/**
 * PavelAuth — Facebook-SDK-style client for the centralized auth service.
 *
 * One <script src="<authBaseUrl>/sdk.js"> include exposes a global `PavelAuth`.
 * The SDK holds tokens, auto-refreshes on 401, and exposes `authFetch`.
 *
 * Security note: `getUser()` decodes JWT claims for DISPLAY ONLY and never
 * verifies the signature. Servers must still verify via JWKS.
 */

import {
  parseFragment,
  isTrustedMessage,
  decodeUser,
  type User,
  type AuthMessage,
} from './helpers.js';

export {
  parseFragment,
  isTrustedMessage,
  decodeUser,
  type User,
  type ParsedFragment,
  type AuthMessage,
} from './helpers.js';

export interface Session {
  /** OAuth-only, server-verified metadata. Absent on restore/refresh and old servers. */
  authCompletion?: Promise<AuthCompletion | null>;
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  user: User | null;
}

export interface StorageAdapter {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export interface InitOptions {
  authBaseUrl: string;
  appUrl?: string;
  storage?: 'local' | 'memory' | StorageAdapter;
  onLogin?: (user: User | null) => void;
  onLogout?: () => void;
}

export interface LoginOptions {
  /** Echoed back untouched (e.g. a return path). */
  state?: string;
}

export interface ButtonOptions {
  text?: string;
  state?: string;
}

const REFRESH_KEY = 'pavel-auth:refresh_token';

function originOf(url: string): string {
  return new URL(url).origin;
}

// ---------------------------------------------------------------------------
// Storage
// ---------------------------------------------------------------------------

function memoryStorage(): StorageAdapter {
  const map = new Map<string, string>();
  return {
    get: (k) => (map.has(k) ? (map.get(k) as string) : null),
    set: (k, v) => {
      map.set(k, v);
    },
    remove: (k) => {
      map.delete(k);
    },
  };
}

function localStorageAdapter(): StorageAdapter {
  return {
    get: (k) => {
      try {
        return window.localStorage.getItem(k);
      } catch {
        return null;
      }
    },
    set: (k, v) => {
      try {
        window.localStorage.setItem(k, v);
      } catch {
        /* ignore */
      }
    },
    remove: (k) => {
      try {
        window.localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    },
  };
}

function resolveRefreshStore(opt: InitOptions['storage']): StorageAdapter {
  if (opt === 'memory') return memoryStorage();
  if (opt && typeof opt === 'object') return opt;
  // 'local' or undefined: refresh token persisted in localStorage.
  return localStorageAdapter();
}

// ---------------------------------------------------------------------------
// SDK state
// ---------------------------------------------------------------------------

interface InternalState {
  config: {
    authBaseUrl: string;
    appUrl: string;
    authOrigin: string;
    onLogin?: (user: User | null) => void;
    onLogout?: () => void;
  };
  refreshStore: StorageAdapter;
  accessToken: string | null; // always in memory
  expiresIn: number; // seconds, from the last token issuance
}

let state: InternalState | null = null;

/** In-flight restore() promise, so concurrent calls share one refresh. */
let restoreInFlight: Promise<Session | null> | null = null;

function ensureInit(): InternalState {
  if (!state) throw new Error('PavelAuth.init() must be called before use.');
  return state;
}

function getRefreshToken(): string | null {
  return state ? state.refreshStore.get(REFRESH_KEY) : null;
}

function setTokens(
  s: InternalState,
  accessToken: string,
  refreshToken: string | null,
  expiresIn: number,
): void {
  s.accessToken = accessToken;
  s.expiresIn = expiresIn;
  if (refreshToken) s.refreshStore.set(REFRESH_KEY, refreshToken);
}

function clearTokens(s: InternalState): void {
  s.accessToken = null;
  s.expiresIn = 0;
  s.refreshStore.remove(REFRESH_KEY);
}

function sessionFromTokens(
  accessToken: string,
  refreshToken: string | null,
  expiresIn: number,
): Session {
  return {
    accessToken,
    refreshToken,
    expiresIn,
    user: decodeUser(accessToken),
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function init(options: InitOptions): void {
  const authBaseUrl = options.authBaseUrl.replace(/\/+$/, '');
  const appUrl = options.appUrl ?? window.location.origin;
  state = {
    config: {
      authBaseUrl,
      appUrl,
      authOrigin: originOf(authBaseUrl),
      ...(options.onLogin ? { onLogin: options.onLogin } : {}),
      ...(options.onLogout ? { onLogout: options.onLogout } : {}),
    },
    refreshStore: resolveRefreshStore(options.storage),
    accessToken: null,
    expiresIn: 0,
  };
  restoreInFlight = null;
}

function buildLoginUrl(mode: 'popup' | 'redirect', opts?: LoginOptions): string {
  const s = ensureInit();
  const url = new URL(`${s.config.authBaseUrl}/auth/login`);
  url.searchParams.set('redirect_uri', s.config.appUrl);
  url.searchParams.set('mode', mode);
  if (opts?.state !== undefined) url.searchParams.set('state', opts.state);
  return url.toString();
}

function login(opts?: LoginOptions): Promise<Session> {
  const s = ensureInit();
  const popupUrl = buildLoginUrl('popup', opts);

  return new Promise<Session>((resolve, reject) => {
    const popup = window.open(popupUrl, 'pavel-auth', 'width=480,height=640');

    if (!popup) {
      // Popups blocked → full-tab redirect fallback. The app consumes the
      // fragment on next load via handleRedirectCallback().
      window.location.assign(buildLoginUrl('redirect', opts));
      return;
    }

    let settled = false;
    const onMessage = (event: MessageEvent): void => {
      if (!isTrustedMessage(event.data, event.origin, s.config.authOrigin)) return;
      const msg = event.data as AuthMessage;
      settled = true;
      cleanup();
      setTokens(s, msg.access_token, msg.refresh_token ?? null, msg.expires_in);
      const session = sessionFromTokens(
        msg.access_token,
        msg.refresh_token ?? null,
        msg.expires_in,
      );
      const receipt = (msg as AuthMessage & { auth_completion?: unknown }).auth_completion;
      if (receipt) session.authCompletion = confirmAuthCompletion(s.config.authBaseUrl, receipt, msg.access_token);
      s.config.onLogin?.(session.user);
      resolve(session);
    };

    // Poll for the user manually closing the popup. Under COOP unsafe-none the
    // popup is cross-origin, so reading `popup.closed` may be blocked/throw;
    // swallow that — success still arrives via postMessage. We only treat the
    // popup as "closed by the user" when the read succeeds and reports true.
    const interval = window.setInterval(() => {
      if (settled) return;
      let closed = false;
      try {
        closed = popup.closed;
      } catch {
        return; // COOP blocked the read; ignore and keep waiting for a message.
      }
      if (closed) {
        cleanup();
        reject(new Error('Sign-in popup closed before completing.'));
      }
    }, 500);

    function cleanup(): void {
      window.removeEventListener('message', onMessage);
      window.clearInterval(interval);
    }

    window.addEventListener('message', onMessage);
  });
}

/** Consume tokens from the URL fragment on app load (redirect-mode fallback). */
function handleRedirectCallback(): Session | null {
  const s = ensureInit();
  const receipt = new URLSearchParams(window.location.hash.replace(/^#/, '')).get('auth_completion');
  const parsed = parseFragment(window.location.hash);
  if (!parsed) return null;
  setTokens(s, parsed.accessToken, parsed.refreshToken, parsed.expiresIn);
  // Strip the hash so tokens don't linger in the URL / history.
  try {
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search,
    );
  } catch {
    /* ignore */
  }
  const session = sessionFromTokens(parsed.accessToken, parsed.refreshToken, parsed.expiresIn);
  if (receipt) session.authCompletion = confirmAuthCompletion(s.config.authBaseUrl, receipt, parsed.accessToken);
  s.config.onLogin?.(session.user);
  return session;
}

async function logout(): Promise<void> {
  const s = ensureInit();
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await fetch(`${s.config.authBaseUrl}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      /* best-effort; clear locally regardless */
    }
  }
  clearTokens(s);
  s.config.onLogout?.();
}

/**
 * Restore a session from the persisted refresh token (silent auth).
 *
 * Call once on app load after init() to learn the REAL auth status without a
 * "warm-up" protected request:
 *   await PavelAuth.init(...);
 *   const session = await PavelAuth.restore();  // null if not signed in
 *
 * - refresh token present & accepted → new access token in memory, rotated
 *   refresh token persisted, onLogin(user) fired, resolves Session.
 * - refresh token absent or rejected → store cleared, onLogout() fired,
 *   resolves null.
 * Idempotent: concurrent/repeat calls share one in-flight refresh.
 */
function restore(): Promise<Session | null> {
  const s = ensureInit();
  if (restoreInFlight) return restoreInFlight;

  const run = (async (): Promise<Session | null> => {
    if (!getRefreshToken()) {
      // Nothing to restore; treat as a clean signed-out state.
      clearTokens(s);
      s.config.onLogout?.();
      return null;
    }
    const refreshed = await refreshTokens(s);
    if (!refreshed || !s.accessToken) {
      clearTokens(s);
      s.config.onLogout?.();
      return null;
    }
    const session = sessionFromTokens(s.accessToken, getRefreshToken(), s.expiresIn);
    s.config.onLogin?.(session.user);
    return session;
  })();

  // Latch the in-flight promise so concurrent calls share one refresh, then
  // release it once settled so a later restore() can run again.
  restoreInFlight = run;
  void run.finally(() => {
    if (restoreInFlight === run) restoreInFlight = null;
  });
  return run;
}

function getAccessToken(): string | null {
  return state ? state.accessToken : null;
}

function getUser(): User | null {
  return decodeUser(getAccessToken());
}

function isAuthenticated(): boolean {
  return getAccessToken() !== null;
}

async function refreshTokens(s: InternalState): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await fetch(`${s.config.authBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;
    const body = (await res.json()) as {
      success: boolean;
      data?: { access_token: string; refresh_token: string; expires_in: number };
    };
    if (!body.success || !body.data) return false;
    setTokens(s, body.data.access_token, body.data.refresh_token, body.data.expires_in);
    return true;
  } catch {
    return false;
  }
}

function withBearer(init: RequestInit | undefined, token: string): RequestInit {
  const headers = new Headers(init?.headers ?? {});
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
}

/** fetch wrapper: injects Bearer; on 401, refreshes once and retries. */
async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const s = ensureInit();
  const token = s.accessToken;

  const firstInit = token ? withBearer(init, token) : init;
  let res = await fetch(input, firstInit);
  if (res.status !== 401) return res;

  const refreshed = await refreshTokens(s);
  if (!refreshed) {
    clearTokens(s);
    s.config.onLogout?.();
    return res; // surface the original 401
  }

  res = await fetch(input, withBearer(init, s.accessToken as string));
  return res;
}

function renderButton(target: string | Element, opts?: ButtonOptions): void {
  ensureInit();
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) throw new Error('PavelAuth.renderButton: target element not found.');

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Sign in with Google');
  btn.style.cssText = [
    'display:inline-flex',
    'align-items:center',
    'gap:12px',
    'height:40px',
    'padding:0 16px',
    'border:1px solid #dadce0',
    'border-radius:4px',
    'background:#fff',
    'color:#3c4043',
    "font:500 14px/1 'Roboto',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    'cursor:pointer',
    'box-shadow:0 1px 2px rgba(60,64,67,.10)',
  ].join(';');

  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('width', '18');
  icon.setAttribute('height', '18');
  icon.setAttribute('viewBox', '0 0 48 48');
  icon.innerHTML =
    '<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>' +
    '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>' +
    '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>' +
    '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>';

  const label = document.createElement('span');
  label.textContent = opts?.text ?? 'Sign in with Google';

  btn.appendChild(icon);
  btn.appendChild(label);
  btn.addEventListener('click', () => {
    void login(opts?.state !== undefined ? { state: opts.state } : undefined).catch(() => {
      /* swallow; consumer can use onLogin/onLogout or login() directly */
    });
  });

  el.appendChild(btn);
}

const PavelAuth = {
  init,
  login,
  handleRedirectCallback,
  restore,
  logout,
  getAccessToken,
  getUser,
  isAuthenticated,
  authFetch,
  renderButton,
  // pure helpers exposed for testing / advanced use
  parseFragment,
  isTrustedMessage,
  decodeUser,
};

export default PavelAuth;
